<!-- src/views/pages/hrm/attendance/performance/performance-review.vue -->
<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper dq-pr">
    <div class="content">
      <!-- Breadcrumb / Actions -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3 dq-noprint"
      >
        <breadcrumb
          title="Performance Review"
          text="Performance"
          text1="Performance Review"
        />

        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="head-icons ms-2 d-flex gap-2 flex-wrap">
            <router-link
              class="btn btn-outline-secondary dq-btn dq-btn-back"
              :to="{ path: '/performance/performance-review-list' }"
            >
              <i class="ti ti-arrow-left me-1"></i> Back
            </router-link>

            <button class="btn btn-outline-secondary dq-btn" @click="printPage">
              <i class="ti ti-printer me-1"></i> Print
            </button>

            <button
              class="btn btn-outline-primary dq-btn"
              @click="downloadPdf"
              :disabled="!editingId"
            >
              <i class="ti ti-download me-1"></i> Download PDF
            </button>

            <button class="btn btn-primary dq-btn dq-btn-primary" @click="save">
              <span
                v-if="reviewStore.loading"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              <i v-else class="ti ti-device-floppy me-1"></i>
              {{ editingId ? "Save Changes" : "Save Review" }}
            </button>
          </div>
        </div>
      </div>

      <!-- KPI / Progress strip -->
      <div class="dq-stats dq-noprint mb-3">
        <div class="dq-stat">
          <div class="dq-stat-top">
            <span class="dq-ico dq-ico-indigo"><i class="ti ti-percentage"></i></span>
            <div>
              <div class="dq-stat-title">Overall (RO)</div>
              <div class="dq-stat-value">{{ overallPercentage }}%</div>
            </div>
          </div>
          <div class="progress dq-progress">
            <div
              class="progress-bar"
              role="progressbar"
              :style="{ width: overallPercentage + '%' }"
              :aria-valuenow="overallPercentage"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        </div>

        <div class="dq-stat">
          <div class="dq-stat-top">
            <span class="dq-ico dq-ico-emerald"><i class="ti ti-trophy"></i></span>
            <div>
              <div class="dq-stat-title">Prof. Excellence (RO)</div>
              <div class="dq-stat-value">{{ totalPctRoPE }}%</div>
            </div>
          </div>
          <div class="dq-muted">Points: {{ totalPtsRoPE }} / {{ totalWeightPE }}</div>
        </div>

        <div class="dq-stat">
          <div class="dq-stat-top">
            <span class="dq-ico dq-ico-amber"><i class="ti ti-user-star"></i></span>
            <div>
              <div class="dq-stat-title">Personal Excellence (RO)</div>
              <div class="dq-stat-value">{{ totalPctRoPers }}%</div>
            </div>
          </div>
          <div class="dq-muted">Points: {{ totalPtsRoPers }} / {{ totalWeightPers }}</div>
        </div>

        <div class="dq-stat">
          <div class="dq-stat-top">
            <span class="dq-ico dq-ico-slate"><i class="ti ti-checklist"></i></span>
            <div>
              <div class="dq-stat-title">Completeness</div>
              <div class="dq-stat-value">{{ completeness }}%</div>
            </div>
          </div>
          <div class="dq-muted">
            Missing: <span class="fw-bold">{{ missingCount }}</span>
          </div>
        </div>
      </div>

      <!-- Header row: meta fields -->
      <div class="card dq-card mb-3">
        <div class="card-body">
          <div class="dq-card-head mb-3">
            <div>
              <div class="dq-card-title">
                <span class="dq-ico dq-ico-indigo"><i class="ti ti-file-description"></i></span>
                Review Details
              </div>
              <div class="dq-card-sub">Select employee/reviewer and review meta.</div>
            </div>
            <div class="d-flex gap-2 align-items-center flex-wrap">
              <span class="dq-chip dq-chip-muted">
                <i class="ti ti-hash"></i>
                ID: <span class="ms-1">{{ editingId || "New" }}</span>
              </span>
              <span class="dq-chip dq-chip-ok" v-if="completeness >= 80">
                <i class="ti ti-circle-check"></i> Ready
              </span>
              <span class="dq-chip dq-chip-warn" v-else>
                <i class="ti ti-alert-triangle"></i> Incomplete
              </span>
            </div>
          </div>

          <div class="row g-3">
            <!-- Employee searchable dropdown -->
            <div class="col-md-4 dq-noprint">
              <label class="form-label dq-label">Employee</label>

              <div class="dropdown w-100">
                <button
                  type="button"
                  class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span class="d-flex align-items-center gap-2 overflow-hidden">
                    <span class="dq-avatar"><i class="ti ti-user"></i></span>
                    <span class="dq-dd-title text-truncate">
                      {{ selectedEmployeeLabel || "Select employee" }}
                    </span>
                  </span>
                  <i class="ti ti-chevron-down"></i>
                </button>

                <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                  <div class="mb-2">
                    <div class="input-icon-start position-relative">
                      <span class="input-icon-addon"><i class="ti ti-search"></i></span>
                      <input
                        v-model.trim="employeeQuery"
                        class="form-control dq-input"
                        placeholder="Search employee…"
                        @keydown.down.prevent="moveEmployeeActive(1)"
                        @keydown.up.prevent="moveEmployeeActive(-1)"
                        @keydown.enter.prevent="selectActiveEmployee()"
                      />
                    </div>
                  </div>

                  <div class="dq-search-list">
                    <button
                      v-for="(opt, idx) in filteredEmployees"
                      :key="'emp-' + opt.id"
                      type="button"
                      class="dropdown-item dq-dd-item"
                      :class="{ active: idx === employeeActiveIndex }"
                      @mouseenter="employeeActiveIndex = idx"
                      @click="pickEmployee(opt.id)"
                    >
                      <div class="dq-dd-row">
                        <div class="dq-avatar"><i class="ti ti-user"></i></div>
                        <div class="flex-grow-1 min-w-0">
                          <div class="dq-dd-title text-truncate">{{ opt.label }}</div>
                          <div class="dq-dd-sub text-truncate">
                            {{ opt.departmentLabel || "—" }}
                            <span v-if="opt.designationLabel"> • {{ opt.designationLabel }}</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <div v-if="!filteredEmployees.length" class="px-2 py-3 text-muted small">
                      No employees found.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-help">↑ ↓ Enter supported. Selecting employee can auto-fill Basic Information.</div>
            </div>

            <!-- Reviewer searchable dropdown -->
            <div class="col-md-4 dq-noprint">
              <label class="form-label dq-label">Reviewer (optional)</label>

              <div class="dropdown w-100">
                <button
                  type="button"
                  class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span class="d-flex align-items-center gap-2 overflow-hidden">
                    <span class="dq-avatar dq-avatar-amber"><i class="ti ti-user-check"></i></span>
                    <span class="dq-dd-title text-truncate">
                      {{ selectedReviewerLabel || "Select reviewer" }}
                    </span>
                  </span>
                  <i class="ti ti-chevron-down"></i>
                </button>

                <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                  <div class="mb-2 d-flex gap-2">
                    <div class="flex-grow-1 input-icon-start position-relative">
                      <span class="input-icon-addon"><i class="ti ti-search"></i></span>
                      <input
                        v-model.trim="reviewerQuery"
                        class="form-control dq-input"
                        placeholder="Search reviewer…"
                        @keydown.down.prevent="moveReviewerActive(1)"
                        @keydown.up.prevent="moveReviewerActive(-1)"
                        @keydown.enter.prevent="selectActiveReviewer()"
                      />
                    </div>

                    <button type="button" class="btn dq-btn dq-btn-muted" @click="clearReviewer()" title="Clear">
                      <i class="ti ti-x"></i>
                    </button>
                  </div>

                  <div class="dq-search-list">
                    <button
                      v-for="(opt, idx) in filteredReviewers"
                      :key="'rev-' + opt.id"
                      type="button"
                      class="dropdown-item dq-dd-item"
                      :class="{ active: idx === reviewerActiveIndex }"
                      @mouseenter="reviewerActiveIndex = idx"
                      @click="pickReviewer(opt.id)"
                    >
                      <div class="dq-dd-row">
                        <div class="dq-avatar dq-avatar-amber"><i class="ti ti-user-check"></i></div>
                        <div class="flex-grow-1 min-w-0">
                          <div class="dq-dd-title text-truncate">{{ opt.label }}</div>
                          <div class="dq-dd-sub text-truncate">
                            {{ opt.departmentLabel || "—" }}
                            <span v-if="opt.designationLabel"> • {{ opt.designationLabel }}</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <div v-if="!filteredReviewers.length" class="px-2 py-3 text-muted small">
                      No reviewers found.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-help">Leave blank if self-review only.</div>
            </div>

            <div class="col-md-2">
              <label class="form-label dq-label">Appraisal Date</label>
              <input v-model="form.appraisal_date" type="date" class="form-control dq-input" />
            </div>

            <div class="col-md-2">
              <label class="form-label dq-label">Status</label>
              <select v-model="form.status" class="form-select dq-input">
                <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>

            <div class="col-12">
              <label class="form-label dq-label">Overall Comment</label>
              <textarea
                v-model="form.overall_comment"
                rows="2"
                class="form-control dq-input"
                placeholder="Summary comment…"
              ></textarea>
            </div>
          </div>

          <!-- print-friendly meta -->
          <div class="dq-print-meta dq-onlyprint mt-3">
            <div><b>Employee:</b> {{ selectedEmployeeLabel || "-" }}</div>
            <div><b>Reviewer:</b> {{ selectedReviewerLabel || "-" }}</div>
            <div><b>Date:</b> {{ form.appraisal_date || "-" }}</div>
            <div><b>Status:</b> {{ form.status || "-" }}</div>
          </div>
        </div>
      </div>

      <!-- Employee Basic Information -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Employee Basic Information</h3>
          <p class="text-muted mb-0">Enter employee and RO details</p>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="table-responsive">
              <table class="table table-bordered table-nowrap mb-0 dq-table">
                <tbody>
                  <tr>
                    <td>
                      <div class="mb-3">
                        <label class="form-label dq-label">Name</label>
                        <input v-model="form.content.basic.name" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">Department</label>
                        <input v-model="form.content.basic.department" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">Designation</label>
                        <input v-model="form.content.basic.designation" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">Qualification</label>
                        <input v-model="form.content.basic.qualification" class="form-control dq-input" />
                      </div>
                    </td>

                    <td>
                      <div class="mb-3">
                        <label class="form-label dq-label">Emp ID</label>
                        <input v-model="form.content.basic.emp_id" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">Date of Join</label>
                        <input v-model="form.content.basic.doj" type="date" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">Date of Confirmation</label>
                        <input v-model="form.content.basic.doc" type="date" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">Previous years of Exp</label>
                        <input v-model="form.content.basic.prev_experience" class="form-control dq-input" />
                      </div>
                    </td>

                    <td>
                      <div class="mb-3">
                        <label class="form-label dq-label">RO's Name</label>
                        <input v-model="form.content.basic.ro_name" class="form-control dq-input" />
                      </div>
                      <div class="mb-3">
                        <label class="form-label dq-label">RO Designation</label>
                        <input v-model="form.content.basic.ro_designation" class="form-control dq-input" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <!-- Professional Excellence -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Professional Excellence</h3>
          <p class="text-muted mb-0">KRA / KPI based scoring</p>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="table-responsive">
              <table class="table table-bordered mb-0 dq-table">
                <thead>
                  <tr>
                    <th class="width-pixel">#</th>
                    <th>Key Result Area</th>
                    <th>Key Performance Indicators</th>
                    <th>Weightage</th>
                    <th>% achieved (Self)</th>
                    <th>Points (Self)</th>
                    <th>% achieved (RO)</th>
                    <th>Points (RO)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, i) in form.content.professional_excellence.kras"
                    :key="`kra-${i}`"
                  >
                    <td>{{ i + 1 }}</td>
                    <td>{{ row.kra }}</td>
                    <td>{{ row.kpi }}</td>
                    <td>
                      <input class="form-control dq-input" type="number" v-model.number="row.weight" readonly />
                    </td>
                    <td>
                      <input class="form-control dq-input" type="number" v-model.number="row.self_pct" />
                    </td>
                    <td>
                      <input class="form-control dq-input" type="number" :value="calcPoints(row.self_pct, row.weight)" readonly />
                    </td>
                    <td>
                      <input class="form-control dq-input" type="number" v-model.number="row.ro_pct" />
                    </td>
                    <td>
                      <input class="form-control dq-input" type="number" :value="calcPoints(row.ro_pct, row.weight)" readonly />
                    </td>
                  </tr>

                  <tr class="dq-total">
                    <td colspan="3" class="text-center fw-bold">Total</td>
                    <td><input class="form-control dq-input" :value="totalWeightPE" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPctSelfPE" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPtsSelfPE" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPctRoPE" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPtsRoPE" readonly /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <!-- Personal Excellence -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Personal Excellence</h3>
          <p class="text-muted mb-0">Attributes and attitude scoring</p>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="table-responsive">
              <table class="table table-bordered mb-0 dq-table">
                <thead>
                  <tr>
                    <th class="width-pixel">#</th>
                    <th>Personal Attributes</th>
                    <th>Key Indicators</th>
                    <th>Weightage</th>
                    <th>% achieved (Self)</th>
                    <th>Points (Self)</th>
                    <th>% achieved (RO)</th>
                    <th>Points (RO)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, i) in form.content.personal_excellence.rows"
                    :key="`pe-${i}`"
                  >
                    <td>{{ i + 1 }}</td>
                    <td>{{ row.attr }}</td>
                    <td>{{ row.indicator }}</td>
                    <td><input class="form-control dq-input" type="number" v-model.number="row.weight" readonly /></td>
                    <td><input class="form-control dq-input" type="number" v-model.number="row.self_pct" /></td>
                    <td><input class="form-control dq-input" type="number" :value="calcPoints(row.self_pct, row.weight)" readonly /></td>
                    <td><input class="form-control dq-input" type="number" v-model.number="row.ro_pct" /></td>
                    <td><input class="form-control dq-input" type="number" :value="calcPoints(row.ro_pct, row.weight)" readonly /></td>
                  </tr>

                  <tr class="dq-total">
                    <td colspan="3" class="text-center fw-bold">Total</td>
                    <td><input class="form-control dq-input" :value="totalWeightPers" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPctSelfPers" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPtsSelfPers" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPctRoPers" readonly /></td>
                    <td><input class="form-control dq-input" :value="totalPtsRoPers" readonly /></td>
                  </tr>

                  <tr>
                    <td colspan="3" class="text-center"><b>Total Percentage(%)</b></td>
                    <td colspan="5" class="text-center">
                      <input class="form-control dq-input" :value="overallPercentage" readonly />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <!-- Special Initiatives -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Special Initiatives, Achievements, Contributions</h3>
          <p class="text-muted mb-0">Add up to 5</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="table-responsive">
              <table class="table table-bordered table-review mb-0 dq-table">
                <thead>
                  <tr>
                    <th class="width-pixel">#</th>
                    <th>By Self</th>
                    <th>RO's Comment</th>
                    <th>HOD's Comment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(v, i) in form.content.achievements.items"
                    :key="`ach-${i}`"
                  >
                    <td>{{ i + 1 }}</td>
                    <td>
                      <input class="form-control dq-input" v-model="form.content.achievements.items[i]" />
                    </td>
                    <td>
                      <input class="form-control dq-input" v-model="form.content.role_comments.items[i]" />
                    </td>
                    <td>
                      <input class="form-control dq-input" v-model="form.content.strengths_ro.rows[i]" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <!-- Strengths -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Employee Strengths & Areas for Improvement (Self)</h3>
          <p class="text-muted mb-0">Top items</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Strengths</th>
                  <th>Areas for Improvement</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(v, i) in 5" :key="`ss-${i}`">
                  <td>{{ i + 1 }}</td>
                  <td><input class="form-control dq-input" v-model="form.content.strengths_self.rows[i]" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.strengths_hod.rows[i]" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Personal Goals -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Personal Goals</h3>
          <p class="text-muted mb-0">Last year vs current year</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Goal Achieved (Last Year)</th>
                  <th>Goal Set (Current Year)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(v, i) in 3" :key="`g-${i}`">
                  <td>{{ i + 1 }}</td>
                  <td><input class="form-control dq-input" v-model="form.content.goals.last_year[i]" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.goals.current_year[i]" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Personal Updates -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Personal Updates</h3>
          <p class="text-muted mb-0">Yes/No + details</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Last Year</th>
                  <th>Yes/No</th>
                  <th>Details</th>
                  <th>Current Year</th>
                  <th>Yes/No</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in form.content.personal_updates.last_year"
                  :key="`pu-${i}`"
                >
                  <td>{{ i + 1 }}</td>
                  <td>{{ row.question }}</td>
                  <td>
                    <select class="form-select dq-input" v-model="row.answer">
                      <option :value="null">Select</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </td>
                  <td><input class="form-control dq-input" v-model="row.details" /></td>

                  <td>{{ form.content.personal_updates.current_year[i].question }}</td>
                  <td>
                    <select
                      class="form-select dq-input"
                      v-model="form.content.personal_updates.current_year[i].answer"
                    >
                      <option :value="null">Select</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </td>
                  <td>
                    <input
                      class="form-control dq-input"
                      v-model="form.content.personal_updates.current_year[i].details"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Training Requirements -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Training Requirements</h3>
          <p class="text-muted mb-0">To meet target standards</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>By Self</th>
                  <th>RO's Comment</th>
                  <th>HOD's Comment</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(v, i) in 5" :key="`tr-${i}`">
                  <td>{{ i + 1 }}</td>
                  <td><input class="form-control dq-input" v-model="form.content.training_requirements.items[i]" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.general_comments.rows[i].ro" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.general_comments.rows[i].hod" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- General Comments -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">Any other general comments</h3>
          <p class="text-muted mb-0">Self / RO / HOD</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Self</th>
                  <th>RO</th>
                  <th>HOD</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in form.content.general_comments.rows"
                  :key="`gc-${i}`"
                >
                  <td>{{ i + 1 }}</td>
                  <td><input class="form-control dq-input" v-model="row.self" /></td>
                  <td><input class="form-control dq-input" v-model="row.ro" /></td>
                  <td><input class="form-control dq-input" v-model="row.hod" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- For RO -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">For RO's Use Only</h3>
          <p class="text-muted mb-0">Observations</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Yes/No</th>
                  <th>If Yes - Details</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in form.content.ro_use_only"
                  :key="`ro-${i}`"
                >
                  <td>{{ row.label }}</td>
                  <td>
                    <select class="form-select dq-input" v-model="row.answer">
                      <option :value="null">Select</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </td>
                  <td><input class="form-control dq-input" v-model="row.details" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- For HRD -->
      <section class="card border-0 dq-card mb-3">
        <div class="card-header dq-card-header text-center">
          <h3 class="mb-1 dq-h3">For HRD's Use Only</h3>
          <p class="text-muted mb-0">Overall parameters</p>
        </div>

        <div class="row">
          <div class="col-md-12">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th>Overall Parameters</th>
                  <th>Available Points</th>
                  <th>Points Scored</th>
                  <th>RO's Comment</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in form.content.hrd_use_only"
                  :key="`hrd-${i}`"
                >
                  <td>{{ row.label }}</td>
                  <td><input class="form-control dq-input" v-model.number="row.available" /></td>
                  <td><input class="form-control dq-input" v-model.number="row.scored" /></td>
                  <td><input class="form-control dq-input" v-model="row.ro_comment" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Signatures -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="table-responsive">
            <table class="table table-bordered mb-0 dq-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Signature</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Employee</td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.employee.name" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.employee.signature" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.employee.date" type="date" /></td>
                </tr>
                <tr>
                  <td>Reporting Officer</td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.ro.name" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.ro.signature" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.ro.date" type="date" /></td>
                </tr>
                <tr>
                  <td>HOD</td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.hod.name" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.hod.signature" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.hod.date" type="date" /></td>
                </tr>
                <tr>
                  <td>HRD</td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.hrd.name" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.hrd.signature" /></td>
                  <td><input class="form-control dq-input" v-model="form.content.signatures.hrd.date" type="date" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 dq-noprint">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p class="mb-0">
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePerformanceReviewStore } from "@/stores/performanceReviewStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { toast } from "@/utils/toast";

const router = useRouter();
const route = useRoute();
const reviewStore = usePerformanceReviewStore();
const empStore = useEmployeeStore();

const statusOptions = ["Draft", "Submitted", "Reviewed", "Archived"];
const editingId = computed(() => (route.query.id ? Number(route.query.id) : null));

const form = ref(blankForm());

function blankForm() {
  return {
    employee_id: "",
    reviewer_id: "",
    appraisal_date: null, // yyyy-mm-dd
    status: "Draft",
    overall_comment: "",
    content: {
      basic: {
        name: "",
        department: "",
        designation: "",
        qualification: "",
        emp_id: "",
        doj: "",
        doc: "",
        prev_experience: "",
        ro_name: "",
        ro_designation: "",
      },
      professional_excellence: {
        kras: [
          { kra: "Production", kpi: "Quality", weight: 30, self_pct: null, ro_pct: null },
          { kra: "Production", kpi: "TAT (turn around time)", weight: 30, self_pct: null, ro_pct: null },
          { kra: "Process Improvement", kpi: "PMS, New Ideas", weight: 10, self_pct: null, ro_pct: null },
          { kra: "Team Management", kpi: "Productivity, attendance, attrition", weight: 5, self_pct: null, ro_pct: null },
          { kra: "Knowledge Sharing", kpi: "Sharing for team productivity", weight: 5, self_pct: null, ro_pct: null },
          { kra: "Reporting & Communication", kpi: "Emails/Calls/Reports/Other", weight: 5, self_pct: null, ro_pct: null },
        ],
      },
      personal_excellence: {
        rows: [
          { attr: "Attendance", indicator: "Leaves", weight: 2, self_pct: null, ro_pct: null },
          { attr: "Attendance", indicator: "Time Consciousness", weight: 2, self_pct: null, ro_pct: null },
          { attr: "Attitude & Behavior", indicator: "Team Collaboration", weight: 2, self_pct: null, ro_pct: null },
          { attr: "Attitude & Behavior", indicator: "Professionalism & Responsiveness", weight: 2, self_pct: null, ro_pct: null },
          { attr: "Policy & Procedures", indicator: "Adherence", weight: 2, self_pct: null, ro_pct: null },
          { attr: "Initiatives", indicator: "Special efforts, ideas", weight: 2, self_pct: null, ro_pct: null },
          { attr: "Continuous Skill Improvement", indicator: "Preparedness/Training", weight: 3, self_pct: null, ro_pct: null },
        ],
      },
      achievements: { items: ["", "", "", "", ""] },
      role_comments: { items: ["", "", "", "", ""] },
      strengths_self: { rows: ["", "", "", "", ""] },
      strengths_ro: { rows: ["", "", "", "", ""] },
      strengths_hod: { rows: ["", "", "", "", ""] },
      goals: { last_year: ["", "", ""], current_year: ["", "", ""] },
      personal_updates: {
        last_year: [
          { question: "Married/Engaged?", answer: null, details: "" },
          { question: "Higher Studies/Certifications?", answer: null, details: "" },
          { question: "Health Issues?", answer: null, details: "" },
          { question: "Others", answer: null, details: "" },
        ],
        current_year: [
          { question: "Marriage Plans", answer: null, details: "" },
          { question: "Plans For Higher Study", answer: null, details: "" },
          { question: "Certification Plans", answer: null, details: "" },
          { question: "Others", answer: null, details: "" },
        ],
      },
      training_requirements: { items: ["", "", "", "", ""] },
      general_comments: {
        rows: [
          { self: "", ro: "", hod: "" },
          { self: "", ro: "", hod: "" },
          { self: "", ro: "", hod: "" },
          { self: "", ro: "", hod: "" },
          { self: "", ro: "", hod: "" },
        ],
      },
      ro_use_only: [
        { label: "Work related Issues", answer: null, details: "" },
        { label: "Leave Issues", answer: null, details: "" },
        { label: "Stability Issues", answer: null, details: "" },
        { label: "Non-supportive attitude", answer: null, details: "" },
        { label: "Other specific notes", answer: null, details: "" },
        { label: "Overall Comment / Performance", answer: null, details: "" },
      ],
      hrd_use_only: [
        { label: "KRAs Target Achievement Points", available: null, scored: null, ro_comment: "" },
        { label: "Professional Skills Scores", available: null, scored: null, ro_comment: "" },
        { label: "Personal Skills Scores", available: null, scored: null, ro_comment: "" },
        { label: "Special Achievements Score", available: null, scored: null, ro_comment: "" },
        { label: "Overall Total Score", available: null, scored: null, ro_comment: "" },
      ],
      signatures: {
        employee: { name: "", signature: "", date: "" },
        ro: { name: "", signature: "", date: "" },
        hod: { name: "", signature: "", date: "" },
        hrd: { name: "", signature: "", date: "" },
      },
    },
  };
}

/* ---------------- Employee search dropdowns ---------------- */
const employeeQuery = ref("");
const reviewerQuery = ref("");
const employeeActiveIndex = ref(0);
const reviewerActiveIndex = ref(0);

const employeesForSelect = computed(() =>
  (empStore.employees || []).map((e) => ({
    id: e.id,
    label: `${e?.user?.first_name ?? e.first_name ?? ""} ${e?.user?.last_name ?? e.last_name ?? ""}`.trim(),
    departmentLabel: e?.department?.name ?? e?.department_name ?? "",
    designationLabel: e?.designation?.name ?? e?.designation_name ?? "",
    raw: e,
  }))
);

const filteredEmployees = computed(() => {
  const q = employeeQuery.value.trim().toLowerCase();
  if (!q) return employeesForSelect.value;
  return employeesForSelect.value.filter((x) => (x.label || "").toLowerCase().includes(q));
});

const filteredReviewers = computed(() => {
  const q = reviewerQuery.value.trim().toLowerCase();
  if (!q) return employeesForSelect.value;
  return employeesForSelect.value.filter((x) => (x.label || "").toLowerCase().includes(q));
});

const selectedEmployeeLabel = computed(() => {
  const id = form.value.employee_id;
  if (!id) return "";
  return employeesForSelect.value.find((x) => String(x.id) === String(id))?.label || "";
});
const selectedReviewerLabel = computed(() => {
  const id = form.value.reviewer_id;
  if (!id) return "";
  return employeesForSelect.value.find((x) => String(x.id) === String(id))?.label || "";
});

function moveEmployeeActive(dir) {
  const len = filteredEmployees.value.length;
  if (!len) return;
  employeeActiveIndex.value = (employeeActiveIndex.value + dir + len) % len;
  scrollActiveIntoView();
}
function selectActiveEmployee() {
  const opt = filteredEmployees.value[employeeActiveIndex.value];
  if (opt) pickEmployee(opt.id);
}
function pickEmployee(id) {
  form.value.employee_id = id;
  employeeQuery.value = "";
  employeeActiveIndex.value = 0;
  autoFillBasicFromEmployee(id);
  document.activeElement?.blur?.();
}

function moveReviewerActive(dir) {
  const len = filteredReviewers.value.length;
  if (!len) return;
  reviewerActiveIndex.value = (reviewerActiveIndex.value + dir + len) % len;
  scrollActiveIntoView();
}
function selectActiveReviewer() {
  const opt = filteredReviewers.value[reviewerActiveIndex.value];
  if (opt) pickReviewer(opt.id);
}
function pickReviewer(id) {
  form.value.reviewer_id = id;
  reviewerQuery.value = "";
  reviewerActiveIndex.value = 0;
  document.activeElement?.blur?.();
}
function clearReviewer() {
  form.value.reviewer_id = "";
  reviewerQuery.value = "";
  reviewerActiveIndex.value = 0;
}

async function scrollActiveIntoView() {
  await nextTick();
  // find any open dropdown list
  const lists = document.querySelectorAll(".dq-dd-menu .dq-search-list");
  const list = Array.from(lists).find((el) => el.offsetParent !== null);
  if (!list) return;
  const active = list.querySelector(".dropdown-item.active");
  active?.scrollIntoView({ block: "nearest" });
}

/* Auto-fill basic info from selected employee (only if empty) */
function autoFillBasicFromEmployee(id) {
  const emp = employeesForSelect.value.find((x) => String(x.id) === String(id))?.raw;
  if (!emp) return;

  const fullName = `${emp?.user?.first_name ?? emp.first_name ?? ""} ${emp?.user?.last_name ?? emp.last_name ?? ""}`.trim();

  if (!form.value.content.basic.name) form.value.content.basic.name = fullName;
  if (!form.value.content.basic.emp_id) form.value.content.basic.emp_id = emp?.employee_code ?? emp?.emp_id ?? emp?.employee_id ?? "";
  if (!form.value.content.basic.department) form.value.content.basic.department = emp?.department?.name ?? emp?.department_name ?? "";
  if (!form.value.content.basic.designation) form.value.content.basic.designation = emp?.designation?.name ?? emp?.designation_name ?? "";

  const managerName =
    emp?.manager_name ||
    [emp?.manager?.first_name, emp?.manager?.last_name].filter(Boolean).join(" ").trim();
  if (managerName && !form.value.content.signatures.ro.name) {
    form.value.content.signatures.ro.name = managerName;
  }
}

/* Keep reviewer != employee */
watch(
  () => [form.value.employee_id, form.value.reviewer_id],
  ([emp, rev]) => {
    if (emp && rev && String(emp) === String(rev)) {
      form.value.reviewer_id = "";
      toast("Reviewer cannot be the same as Employee", "error");
    }
  }
);

/* ---------------- Scoring utils ---------------- */
function calcPoints(pct, weight) {
  const p = Number(pct) || 0;
  const w = Number(weight) || 0;
  return Math.round((p * w) / 100);
}
function sum(arr) {
  return arr.reduce((a, b) => a + (Number(b) || 0), 0);
}
function pctFromPoints(points, weight) {
  return weight ? Math.round((points / weight) * 100) : 0;
}

/* Totals - Professional Excellence */
const totalWeightPE = computed(() =>
  sum(form.value.content.professional_excellence.kras.map((r) => Number(r.weight) || 0))
);
const totalPtsSelfPE = computed(() =>
  sum(form.value.content.professional_excellence.kras.map((r) => calcPoints(r.self_pct, r.weight)))
);
const totalPtsRoPE = computed(() =>
  sum(form.value.content.professional_excellence.kras.map((r) => calcPoints(r.ro_pct, r.weight)))
);
const totalPctSelfPE = computed(() => pctFromPoints(totalPtsSelfPE.value, totalWeightPE.value));
const totalPctRoPE = computed(() => pctFromPoints(totalPtsRoPE.value, totalWeightPE.value));

/* Totals - Personal Excellence */
const totalWeightPers = computed(() =>
  sum(form.value.content.personal_excellence.rows.map((r) => Number(r.weight) || 0))
);
const totalPtsSelfPers = computed(() =>
  sum(form.value.content.personal_excellence.rows.map((r) => calcPoints(r.self_pct, r.weight)))
);
const totalPtsRoPers = computed(() =>
  sum(form.value.content.personal_excellence.rows.map((r) => calcPoints(r.ro_pct, r.weight)))
);
const totalPctSelfPers = computed(() => pctFromPoints(totalPtsSelfPers.value, totalWeightPers.value));
const totalPctRoPers = computed(() => pctFromPoints(totalPtsRoPers.value, totalWeightPers.value));

/* Overall */
const overallPercentage = computed(() => {
  const totW = totalWeightPE.value + totalWeightPers.value;
  const pts = totalPtsRoPE.value + totalPtsRoPers.value;
  return totW ? Math.round((pts / totW) * 100) : 0;
});

/* Completeness KPI */
const missingFields = computed(() => {
  const missing = [];
  if (!form.value.employee_id) missing.push("Employee");
  if (!form.value.appraisal_date) missing.push("Appraisal Date");
  if (!form.value.content.basic.name) missing.push("Basic: Name");
  if (!form.value.content.basic.department) missing.push("Basic: Department");
  if (!form.value.content.basic.designation) missing.push("Basic: Designation");
  return missing;
});
const missingCount = computed(() => missingFields.value.length);
const completeness = computed(() => {
  const total = 5; // keep simple
  const missing = Math.min(missingCount.value, total);
  return Math.max(0, Math.round(((total - missing) / total) * 100));
});

function toYMD(d) {
  if (!d) return null;
  if (typeof d === "string") return d;
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = `${dt.getMonth() + 1}`.padStart(2, "0");
  const day = `${dt.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function save() {
  if (!form.value.employee_id || !form.value.appraisal_date) {
    return toast("Employee and Appraisal Date are required", "error");
  }

  const payload = {
    employee_id: form.value.employee_id,
    reviewer_id: form.value.reviewer_id || null,
    appraisal_date: toYMD(form.value.appraisal_date),
    status: form.value.status || "Draft",
    overall_comment: form.value.overall_comment || null,
    content: form.value.content,
  };

  try {
    if (editingId.value) {
      await reviewStore.update(editingId.value, payload);
      toast("Review updated successfully", "success");
    } else {
      const saved = await reviewStore.create(payload);
      toast("Review saved successfully", "success");
      router.replace({ path: "/performance/performance-review", query: { id: saved.id } });
    }
  } catch {
    toast("Failed to save review", "error");
  }
}

function printPage() {
  window.print();
}
async function downloadPdf() {
  if (!editingId.value) return toast("Save the review first to generate PDF", "error");
  try {
    await reviewStore.downloadPdf(editingId.value);
  } catch {
    toast("Failed to download PDF", "error");
  }
}

onMounted(async () => {
  if (!empStore.employees?.length && typeof empStore.fetchEmployees === "function") {
    await empStore.fetchEmployees();
  }

  if (editingId.value) {
    const r = await reviewStore.fetchOne(editingId.value);
    form.value.employee_id = r.employee_id ?? "";
    form.value.reviewer_id = r.reviewer_id ?? "";
    form.value.appraisal_date = r.appraisal_date ?? null;
    form.value.status = r.status ?? "Draft";
    form.value.overall_comment = r.overall_comment ?? "";
    form.value.content = r.content ?? form.value.content;

    if (form.value.employee_id) autoFillBasicFromEmployee(form.value.employee_id);
  }
});
</script>

<style scoped>
/* Base */
.dq-pr .dq-card{
  border: 1px solid rgba(15, 23, 42, 0.06) !important;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-card-header{
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.10), #fff);
  border: 1px solid rgba(15, 23, 42, 0.06) !important;
  border-bottom: 0 !important;
  padding: 16px 16px;
}
.dq-h3{ font-weight: 950; letter-spacing: -0.35px; }

/* Head */
.dq-card-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.dq-card-title{ display:flex; align-items:center; gap:10px; font-weight: 950; }
.dq-card-sub{ margin-top: 6px; font-size:.82rem; color: rgba(15, 23, 42, 0.6); }

/* Inputs */
.dq-label{ font-size:.82rem; font-weight: 900; }
.dq-input{
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus{
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.dq-help{ margin-top: 6px; font-size: .8rem; color: rgba(15, 23, 42, 0.55); }

/* Buttons */
.dq-btn{ border-radius: 12px; padding: 10px 14px; font-weight: 900; }
.dq-btn-muted{
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary{ box-shadow: 0 12px 24px rgba(15, 23, 42, 0.10); }

/* KPI strip */
.dq-stats{
  display:grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.dq-stat{
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background:#fff;
  padding: 12px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.04);
}
.dq-stat-top{ display:flex; align-items:center; gap:10px; margin-bottom: 8px; }
.dq-stat-title{ font-size:.8rem; color: rgba(15, 23, 42, 0.6); font-weight: 900; }
.dq-stat-value{ font-size: 1.25rem; font-weight: 950; letter-spacing: -0.35px; }
.dq-muted{ font-size: .82rem; color: rgba(15, 23, 42, 0.6); }

.dq-progress{
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.06);
}
.progress-bar{
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.9);
}

/* Icons */
.dq-ico{
  width: 36px; height: 36px; border-radius: 14px;
  display:flex; align-items:center; justify-content:center;
  font-size: 18px;
}
.dq-ico-indigo{ background: rgba(99, 102, 241, 0.14); color: rgb(79, 70, 229); }
.dq-ico-emerald{ background: rgba(16, 185, 129, 0.14); color: rgb(5, 150, 105); }
.dq-ico-amber{ background: rgba(245, 158, 11, 0.14); color: rgb(217, 119, 6); }
.dq-ico-slate{ background: rgba(15, 23, 42, 0.06); color: rgba(15, 23, 42, 0.78); }

/* Dropdown */
.dq-dd-btn{
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-dd-menu{
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
}
.dq-search-list{
  max-height: 280px;
  overflow:auto;
  overscroll-behavior: contain;
}
.dq-dd-item{
  border-radius: 12px;
  padding: 10px 10px;
}
.dq-dd-item:hover{ background: rgba(15, 23, 42, 0.04); }
.dq-dd-row{ display:flex; gap:10px; align-items:center; }
.dq-avatar{
  width: 34px; height: 34px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.10);
  color: rgb(79, 70, 229);
  display:flex; align-items:center; justify-content:center;
}
.dq-avatar-amber{ background: rgba(245, 158, 11, 0.14); color: rgb(217, 119, 6); }
.dq-dd-title{ font-weight: 900; }
.dq-dd-sub{ font-size: .78rem; color: rgba(15, 23, 42, 0.55); }

/* Chips */
.dq-chip{
  display:inline-flex; align-items:center; gap:8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: .82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-chip-muted{ background: rgba(15, 23, 42, 0.04); color: rgba(15, 23, 42, 0.7); }
.dq-chip-ok{ background: rgba(16, 185, 129, 0.12); color: rgb(5, 150, 105); }
.dq-chip-warn{ background: rgba(245, 158, 11, 0.12); color: rgb(217, 119, 6); }

/* Table */
.dq-table thead th{
  background: rgba(15, 23, 42, 0.02);
  font-size: .82rem;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.72);
}
.dq-total td{ background: rgba(15, 23, 42, 0.02); }

/* Print helpers */
.dq-onlyprint{ display:none; }
@media print{
  .dq-noprint,
  .page-breadcrumb,
  .footer,
  .btn,
  .head-icons{ display:none !important; }
  .dq-onlyprint{ display:block !important; }
  .card{ border:none !important; box-shadow:none !important; }
  .dq-card-header{ background:#fff !important; }
}

/* Responsive */
@media (max-width: 1200px){
  .dq-stats{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 576px){
  .dq-stats{ grid-template-columns: 1fr; }
}

.dq-btn-back {
  width: 80px;
}
</style>


